import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";
import { verify } from "@/helpers/verifyAdmin";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Loader2
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { AreaResult } from "@/types/supabase";
import {
  getAreas,
  createArea,
  updateArea,
  deleteArea
} from "@/utils/supabase/areaHelpers";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const AreaManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [areaToDelete, setAreaToDelete] = useState<AreaResult | null>(null);
  
  const [newAreaName, setNewAreaName] = useState("");
  const [newAreaTerritory, setNewAreaTerritory] = useState("");
  
  const [editAreaName, setEditAreaName] = useState("");
  const [editAreaTerritory, setEditAreaTerritory] = useState("");

  const { data: areas, isLoading } = useQuery({
    queryKey: ["areas"],
    queryFn: getAreas,
  });

  // Get unique territories from areas
  const territories = React.useMemo(() => {
    if (!areas) return [];
    return Array.from(new Set(areas.map(area => area.territory))).sort();
  }, [areas]);

  verify(user, navigate);

  const createMutation = useMutation({
    mutationFn: (newArea: { name: string; territory: string }) => createArea(newArea),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["areas"] });
      toast.success("Area created successfully");
      setIsCreating(false);
      resetCreateForm();
    },
    onError: (error) => {
      toast.error(`Failed to create area: ${error}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; territory?: string } }) => 
      updateArea(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["areas"] });
      toast.success("Area updated successfully");
      setIsEditing(null);
    },
    onError: (error) => {
      toast.error(`Failed to update area: ${error}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteArea(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["areas"] });
      toast.success("Area deleted successfully");
      setAreaToDelete(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete area: ${error}`);
    },
  });

  const handleCreateArea = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAreaName.trim()) {
      toast.error("Area name is required");
      return;
    }

    if (!newAreaTerritory.trim()) {
      toast.error("Territory is required");
      return;
    }

    createMutation.mutate({
      name: newAreaName.trim(),
      territory: newAreaTerritory.trim()
    });
  };

  const handleEditClick = (area: AreaResult) => {
    setIsEditing(area.id);
    setEditAreaName(area.name);
    setEditAreaTerritory(area.territory);
  };

  const handleUpdateArea = (id: string) => {
    if (!editAreaName.trim()) {
      toast.error("Area name is required");
      return;
    }

    if (!editAreaTerritory.trim()) {
      toast.error("Territory is required");
      return;
    }

    updateMutation.mutate({
      id,
      data: {
        name: editAreaName.trim(),
        territory: editAreaTerritory.trim()
      }
    });
  };

  const handleDeleteClick = (area: AreaResult) => {
    setAreaToDelete(area);
  };

  const confirmDelete = () => {
    if (areaToDelete) {
      deleteMutation.mutate(areaToDelete.id);
    }
  };

  const resetCreateForm = () => {
    setNewAreaName("");
    setNewAreaTerritory("");
  };

  if (isLoading) {
    return (
      <AdminLayout title="Manage Areas" loading={isLoading}>
        <div className="container mx-auto py-8">
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Manage Areas" loading={isLoading}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
          </div>
          <Button onClick={() => setIsCreating(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add Area
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Territory</TableHead>
                <TableHead>Area Name</TableHead>
                <TableHead className="w-[150px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {areas && areas.length > 0 ? (
                areas.map((area) => (
                  <TableRow key={area.id}>
                    {isEditing === area.id ? (
                      <>
                        <TableCell>
                          <div className="flex gap-2 items-center">
                            <Select 
                              value={editAreaTerritory} 
                              onValueChange={setEditAreaTerritory}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select territory" />
                              </SelectTrigger>
                              <SelectContent>
                                {territories.map(territory => (
                                  <SelectItem key={territory} value={territory}>
                                    {territory}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              value={editAreaTerritory}
                              onChange={(e) => setEditAreaTerritory(e.target.value)}
                              placeholder="New territory"
                              className="flex-1"
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editAreaName}
                            onChange={(e) => setEditAreaName(e.target.value)}
                            placeholder="Area name"
                          />
                        </TableCell>
                        <TableCell className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleUpdateArea(area.id)}
                            disabled={updateMutation.isPending}
                          >
                            {updateMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setIsEditing(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{area.territory}</TableCell>
                        <TableCell>{area.name}</TableCell>
                        <TableCell className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEditClick(area)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteClick(area)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                    No areas found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Create Area Dialog */}
        <Dialog open={isCreating} onOpenChange={setIsCreating}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Area</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateArea}>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="territory">Territory *</Label>
                  <div className="flex gap-2 items-center">
                    <Select 
                      value={newAreaTerritory} 
                      onValueChange={setNewAreaTerritory}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select territory" />
                      </SelectTrigger>
                      <SelectContent>
                        {territories.map(territory => (
                          <SelectItem key={territory} value={territory}>
                            {territory}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="territory"
                      value={newAreaTerritory}
                      onChange={(e) => setNewAreaTerritory(e.target.value)}
                      placeholder="Or type new territory"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Area Name *</Label>
                  <Input
                    id="name"
                    value={newAreaName}
                    onChange={(e) => setNewAreaName(e.target.value)}
                    placeholder="Area name"
                    required
                  />
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setIsCreating(false);
                    resetCreateForm();
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={
                    createMutation.isPending || 
                    !newAreaName.trim() || 
                    !newAreaTerritory.trim()
                  }
                >
                  {createMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Add Area
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!areaToDelete} onOpenChange={(open) => !open && setAreaToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the area "{areaToDelete?.name}" in {areaToDelete?.territory}.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmDelete}
                className="bg-red-500 hover:bg-red-600"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AreaManagementPage;
